import type { WebhookResponse } from '@monorepo/shared/schemas/webhooks'
import type { ColumnDef } from '@tanstack/react-table'
import {
  Activity,
  AlertTriangle,
  Bot,
  CheckCircle,
  Copy,
  Globe,
  MoreHorizontal,
  Pause,
  Play,
  TestTube,
  Trash2,
} from 'lucide-react'
import { useState } from 'react'
import { EnhancedTable } from '@/shared/components/table/EnhancedTable'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { webhookService } from '../services/webhookService'

interface WebhookTableProps {
  webhooks: WebhookResponse[]
  onWebhookUpdated?: () => void
  onWebhookDeleted?: () => void
}

export function WebhookTable({
  webhooks,
  onWebhookUpdated,
  onWebhookDeleted,
}: WebhookTableProps) {
  const [testingWebhook, setTestingWebhook] = useState<string | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [webhookToDelete, setWebhookToDelete] =
    useState<WebhookResponse | null>(null)

  const handleToggleStatus = async (webhook: WebhookResponse) => {
    try {
      await webhookService.updateWebhook(webhook.id, {
        active: !webhook.active,
      })
      onWebhookUpdated?.()
    } catch (error) {
      toast.error(extractErrorMessage(error))
    }
  }

  const handleTestWebhook = async (webhook: WebhookResponse) => {
    setTestingWebhook(webhook.id)
    try {
      const result = await webhookService.testWebhook(webhook.id)
      if (result.status === 'delivered') {
        toast.success('Webhook test delivered successfully')
      } else {
        toast.error(result.error_message || 'Webhook test failed')
      }
    } catch (error) {
      toast.error(extractErrorMessage(error))
    } finally {
      setTestingWebhook(null)
    }
  }

  const handleDeleteWebhook = async () => {
    if (!webhookToDelete) return

    try {
      await webhookService.deleteWebhook(webhookToDelete.id)
      setDeleteConfirmOpen(false)
      setWebhookToDelete(null)
      onWebhookDeleted?.()
    } catch (error) {
      toast.error(extractErrorMessage(error))
    }
  }

  const copyEndpoint = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      toast.success('Endpoint copied to clipboard')
    } catch (error) {
      toast.error(extractErrorMessage(error))
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const columns: ColumnDef<WebhookResponse>[] = [
    {
      accessorKey: 'description',
      header: 'Description',
      size: 250,
      cell: ({ row }) => (
        <div>
          <div className='font-medium'>
            {row.original.description || row.original.url}
          </div>
          <div className='text-sm text-muted-foreground'>
            Created {formatDate(row.original.created_at)}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'url',
      header: 'Endpoint URL',
      size: 350,
      cell: ({ row }) => (
        <div className='flex items-center gap-2'>
          <Globe className='w-4 h-4 text-muted-foreground flex-shrink-0' />
          <div className='flex-1 min-w-0'>
            <div className='truncate font-mono text-sm'>{row.original.url}</div>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => copyEndpoint(row.original.url)}
              className='h-6 px-2 text-xs text-muted-foreground hover:text-foreground'
            >
              <Copy className='w-3 h-3 mr-1' />
              Copy
            </Button>
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'events',
      header: 'Triggered by',
      size: 120,
      cell: ({ row }) => {
        const events = row.original.events
        const eventCount = events.length
        const agentCount = row.original.filters?.agents?.length || 0

        return (
          <div>
            <div className='font-medium'>{eventCount} events</div>
            <div
              className='text-sm text-muted-foreground truncate'
              title={events.join(', ')}
            >
              {events.join(', ')}
            </div>
            {agentCount > 0 && (
              <Badge variant='outline' className='mt-1 text-xs'>
                <Bot className='w-3 h-3 mr-1' />
                {agentCount} {agentCount === 1 ? 'agent' : 'agents'}
              </Badge>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: 'active',
      header: 'Status',
      size: 120,
      cell: ({ row }) => {
        const isActive = row.original.active
        return (
          <Badge variant={isActive ? 'default' : 'secondary'} className='gap-1'>
            {isActive ? (
              <CheckCircle className='w-4 h-4' />
            ) : (
              <Pause className='w-4 h-4' />
            )}
            {isActive ? 'Active' : 'Inactive'}
          </Badge>
        )
      },
    },
    {
      id: 'activity',
      header: 'Activity',
      size: 250,
      cell: ({ row }) => {
        const stats = row.original.stats
        return (
          <div className='flex items-center gap-2'>
            <Activity className='w-4 h-4 text-muted-foreground' />
            <div>
              <div className='font-medium'>{stats.total_deliveries} calls</div>
              {stats.last_delivery_at && (
                <div className='text-sm text-muted-foreground'>
                  Last: {formatDate(stats.last_delivery_at)}
                </div>
              )}
            </div>
          </div>
        )
      },
    },
    {
      id: 'actions',
      header: '',
      size: 80,
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' size='sm' className='h-8 w-8 p-0'>
              <MoreHorizontal className='w-4 h-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end' className='w-48'>
            <DropdownMenuItem
              onClick={() => handleTestWebhook(row.original)}
              disabled={testingWebhook === row.original.id}
            >
              <TestTube className='w-4 h-4 mr-2' />
              {testingWebhook === row.original.id
                ? 'Testing...'
                : 'Test webhook'}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => copyEndpoint(row.original.url)}>
              <Copy className='w-4 h-4 mr-2' />
              Copy endpoint
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleToggleStatus(row.original)}>
              {row.original.active ? (
                <>
                  <Pause className='w-4 h-4 mr-2' />
                  Pause webhook
                </>
              ) : (
                <>
                  <Play className='w-4 h-4 mr-2' />
                  Activate webhook
                </>
              )}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                setWebhookToDelete(row.original)
                setDeleteConfirmOpen(true)
              }}
              className='text-destructive focus:text-destructive'
            >
              <Trash2 className='w-4 h-4 mr-2' />
              Delete webhook
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ]

  return (
    <>
      <EnhancedTable
        data={webhooks}
        columns={columns}
        className='bg-transparent'
        height='calc(100vh - 400px)'
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <AlertTriangle className='w-5 h-5 text-amber-500' />
              Delete Webhook
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the webhook for "
              {webhookToDelete?.description || webhookToDelete?.url}"? This
              action cannot be undone and will stop all event notifications to
              this endpoint.
            </DialogDescription>
          </DialogHeader>
          <div className='flex justify-end gap-2'>
            <Button
              variant='outline'
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button variant='destructive' onClick={handleDeleteWebhook}>
              Delete Webhook
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
